import { Trash } from 'lucide-react';
import { useState } from 'react';
import {
  useBulkDeleteController,
  useListContext,
  useResourceContext,
  useTranslate,
} from 'ra-core';
import { Confirm } from '../../components/admin/confirm';
import { Button } from '../../components/ui/button';

type MoonstarListBulkActionsProps = {
  label?: string;
  resource?: string;
  successMessage?: string;
  className?: string;
};

/**
 * Bulk delete of the selected records, after a confirmation dialog, waiting for the server (pessimistic).
 * The kit `BulkDeleteButton` deletes straight away with undo instead.
 */
export const MoonstarListBulkActions = ({
  label = 'ra.action.delete',
  successMessage,
  className,
  ...props
}: MoonstarListBulkActionsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const resource = useResourceContext(props);
  const { selectedIds } = useListContext();
  const translate = useTranslate();

  const { handleDelete, isPending } = useBulkDeleteController({
    resource,
    successMessage,
    mutationMode: 'pessimistic',
    mutationOptions: {
      onSettled: () => setIsOpen(false),
    },
  });

  // Our `bulk_delete_*` messages (`englishTranslations.ts`) only use the count
  const translateOptions = { count: selectedIds.length };

  return (
    <>
      <Button
        type="button"
        variant="destructive"
        className={className}
        disabled={isPending}
        onClick={() => setIsOpen(true)}
      >
        <Trash />
        {translate(label, { _: label })}
      </Button>
      <Confirm
        isOpen={isOpen}
        loading={isPending}
        title="ra.message.bulk_delete_title"
        content="ra.message.bulk_delete_content"
        titleTranslateOptions={translateOptions}
        contentTranslateOptions={translateOptions}
        confirmColor="warning"
        onConfirm={handleDelete}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};
